import { createFileRoute, Link, Outlet, useMatch } from '@tanstack/react-router';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { DivWithPrimaryColorVariable } from './-components/div-with-primary-color-variable';
import { Progress } from '@worldcoo/ui/components/progress';
import { ChevronLeftIcon } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/$portalId/$campaignId/donate')({
	loader: ({ context, params }) => {
		context.queryClient.ensureQueryData(
			trpc.portal.getMetadata.queryOptions({
				locale: context.locale,
				portalId: context.portalId,
			}),
		);

		context.queryClient.ensureQueryData(
			trpc.portal.getCampaignMetadata.queryOptions({
				locale: context.locale,
				portalId: context.portalId,
				campaignId: params.campaignId,
			}),
		);
	},
	head: () => ({ meta: [{ name: 'robots', content: 'noindex' }] }),
	component: Component,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const { t } = useLingui();
	const donatePage = useMatch({ from: '/$portalId/$campaignId/donate/', shouldThrow: false });
	const donateDetailsPage = useMatch({ from: '/$portalId/$campaignId/donate/details', shouldThrow: false });

	const backToLink = donatePage
		? '/$portalId/$campaignId'
		: donateDetailsPage
			? '/$portalId/$campaignId/donate'
			: '/$portalId/$campaignId/donate/details';

	return (
		/* The `flex-1` below only fills the viewport if this column is at least as tall as it:
		   the body isn't a flex container, so the height has to be established here. */
		<div className="flex min-h-dvh flex-col">
			<Header />
			<DivWithPrimaryColorVariable className="bg-secondary flex-1">
				<nav className="mx-auto w-full max-w-[1366px]">
					<Link
						to={backToLink}
						params={{ portalId, campaignId }}
						search={(prev) => prev}
						className="flex w-fit items-center gap-2 px-5 py-4 sm:p-6"
					>
						<ChevronLeftIcon />
						{t`Previous`}
					</Link>
				</nav>

				{/* The room below the card while a field is focused is what stops the page from jumping as the
				    keyboard opens. Every step of this flow fits the screen, so the document is only as tall as
				    the viewport: WebKit scrolls down to lift the focused field above the keyboard, has nothing
				    to scroll into, and springs back once the animation ends. Somewhere to scroll keeps that
				    offset valid, and every viewport unit follows the shrunken viewport, `svh` and `lvh`
				    included, so no unit spares us this.

				    Keying the room off the focus instead of off a short viewport is what makes it reliable.
				    iOS only shrinks the visual viewport for the keyboard, so `dvh` and height media queries
				    can stay at their resting value; and where the host app does resize its web view, how much
				    height is left over is a per device number (an iPhone 14 Pro Max keeps ~590px, above any
				    threshold low enough to mean "keyboard" on a smaller phone).

				    Only the elements that actually open a keyboard count, not any `focus-within`: Chrome
				    focuses a button on tap, so a plain `focus-within` grew the page by 60vh every time
				    someone picked a preset amount or hit the primary action. `iframe` is in there for the
				    checkout step, where the card fields live in Adyen's iframes and the focused element the
				    parent document sees is the iframe itself. */}
				<div className="px-5 pb-10 sm:px-6 sm:pb-20 pointer-coarse:has-[:is(input,textarea,iframe):focus]:pb-[60vh]">
					{/* `p-12` on mobile left only ~200px of usable width inside a 390px viewport. */}
					<section className="bg-background shadow-4xl mx-auto flex w-full max-w-xl flex-col gap-6 rounded-xl p-5 sm:gap-8 sm:p-12">
						<DonationProgress />
						<Outlet />
					</section>
				</div>
			</DivWithPrimaryColorVariable>
			<Footer />
		</div>
	);
}

function DonationProgress() {
	const { t } = useLingui();
	const donatePage = useMatch({ from: '/$portalId/$campaignId/donate/', shouldThrow: false });
	const donateDetailsPage = useMatch({ from: '/$portalId/$campaignId/donate/details', shouldThrow: false });

	const step = donatePage ? 1 : donateDetailsPage ? 2 : 3;
	return <Progress className="mx-auto h-1 max-w-50" value={(step / 3) * 100} aria-label={t`Step ${step} of 3`} />;
}
